import React, {useEffect, useMemo, useState} from "react";
import {Switch} from "@headlessui/react";
import {toast} from "react-hot-toast";
import Layout from "../../components/Layout";
import {Pagination} from "../../components/common/pagination";
import PassengerService from "../../services/PassengerService";
import AdminService from "../../services/admin/AdminService";
import {sideBarItems} from "../../utils";

const PASSENGER_TAB = "passenger";
const DRIVER_TAB = "driver";

function RideShareAccessPage() {
  const [activeTab, setActiveTab] = useState(PASSENGER_TAB);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingGlobal, setSavingGlobal] = useState({
    passenger: false,
    driver: false,
  });
  const [passengerEnabled, setPassengerEnabled] = useState(true);
  const [driverEnabled, setDriverEnabled] = useState(true);

  const [passengersLoading, setPassengersLoading] = useState(false);
  const [passengers, setPassengers] = useState([]);
  const [passengerPage, setPassengerPage] = useState(1);
  const [passengerLimit, setPassengerLimit] = useState(15);
  const [passengerTotalPages, setPassengerTotalPages] = useState(1);
  const [passengerSearchQuery, setPassengerSearchQuery] = useState({});
  const [draftPassengerStates, setDraftPassengerStates] = useState({});
  const [savingPassengerIds, setSavingPassengerIds] = useState({});

  const [driversLoading, setDriversLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [driverPage, setDriverPage] = useState(1);
  const [driverLimit, setDriverLimit] = useState(15);
  const [driverTotalPages, setDriverTotalPages] = useState(1);
  const [driverSearchQuery, setDriverSearchQuery] = useState({});
  const [draftDriverStates, setDraftDriverStates] = useState({});
  const [savingDriverIds, setSavingDriverIds] = useState({});

  const getError = err => err?.response?.data?.error || "Operation failed";

  const applyDraftStates = (users, setter, key) => {
    setter(prev => {
      const next = {...prev};
      users.forEach(user => {
        next[user._id] = user?.[key] !== false;
      });
      return next;
    });
  };

  const loadConfig = async () => {
    setLoadingConfig(true);
    try {
      const [passengerAccessRes, driverAccessRes] = await Promise.all([
        PassengerService.getGlobalRideShareAccess(),
        AdminService.getGlobalDriverRideShareAccess(),
      ]);
      setPassengerEnabled(passengerAccessRes?.enabled !== false);
      setDriverEnabled(driverAccessRes?.enabled !== false);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoadingConfig(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const loadPassengers = async ({
    page = passengerPage,
    limit = passengerLimit,
    search = passengerSearchQuery,
  } = {}) => {
    setPassengersLoading(true);
    try {
      const data = await PassengerService.getPassengerPg(page, limit, search);
      const users = Array.isArray(data?.users) ? data.users : [];
      setPassengers(users);
      setPassengerTotalPages(Math.max(1, Number(data?.totalPages || 1)));
      applyDraftStates(
        users,
        setDraftPassengerStates,
        "rideShareAccessEnabled",
      );
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setPassengersLoading(false);
    }
  };

  const loadDrivers = async ({
    page = driverPage,
    limit = driverLimit,
    search = driverSearchQuery,
  } = {}) => {
    setDriversLoading(true);
    try {
      const res = await AdminService.getDriversPg(page, limit, search);
      const data = res?.data || {};
      const users = Array.isArray(data?.users) ? data.users : [];
      setDrivers(users);
      setDriverTotalPages(Math.max(1, Number(data?.totalPages || 1)));
      applyDraftStates(users, setDraftDriverStates, "rideShareAccessEnabled");
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setDriversLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === PASSENGER_TAB) {
      loadPassengers();
    }
  }, [activeTab, passengerPage, passengerLimit]);

  useEffect(() => {
    if (activeTab === DRIVER_TAB) {
      loadDrivers();
    }
  }, [activeTab, driverPage, driverLimit]);

  const saveGlobalAccess = async (type, enabled) => {
    setSavingGlobal(prev => ({...prev, [type]: true}));
    try {
      if (type === PASSENGER_TAB) {
        const data = await PassengerService.updateGlobalRideShareAccess(enabled);
        setPassengerEnabled(data?.enabled !== false);
        setDraftPassengerStates({});
        await loadPassengers({page: passengerPage});
      } else {
        const data = await AdminService.updateGlobalDriverRideShareAccess(
          enabled,
        );
        setDriverEnabled(data?.enabled !== false);
        setDraftDriverStates({});
        await loadDrivers({page: driverPage});
      }

      toast.success("Ride share access updated successfully");
    } catch (err) {
      if (type === PASSENGER_TAB) {
        setPassengerEnabled(prev => !prev);
      } else {
        setDriverEnabled(prev => !prev);
      }
      toast.error(getError(err));
    } finally {
      setSavingGlobal(prev => ({...prev, [type]: false}));
    }
  };

  const savePassengerAccess = async (passengerId, enabled) => {
    setSavingPassengerIds(prev => ({...prev, [passengerId]: true}));
    try {
      const data = await PassengerService.updateRideShareAccess(
        passengerId,
        enabled,
      );
      const updatedPassenger = data?.passenger || null;
      const resolvedEnabled = updatedPassenger?.rideShareAccessEnabled !== false;

      setPassengers(prev =>
        prev.map(item =>
          item._id === passengerId
            ? {...item, rideShareAccessEnabled: resolvedEnabled}
            : item,
        ),
      );
      setDraftPassengerStates(prev => ({...prev, [passengerId]: resolvedEnabled}));
      toast.success("Passenger ride share access updated successfully");
    } catch (err) {
      setDraftPassengerStates(prev => ({
        ...prev,
        [passengerId]: !enabled,
      }));
      toast.error(getError(err));
    } finally {
      setSavingPassengerIds(prev => ({...prev, [passengerId]: false}));
    }
  };

  const saveDriverAccess = async (driverId, enabled) => {
    setSavingDriverIds(prev => ({...prev, [driverId]: true}));
    try {
      const data = await AdminService.updateDriverRideShareAccess(
        driverId,
        enabled,
      );
      const updatedDriver = data?.driver || null;
      const resolvedEnabled = updatedDriver?.rideShareAccessEnabled !== false;

      setDrivers(prev =>
        prev.map(item =>
          item._id === driverId
            ? {...item, rideShareAccessEnabled: resolvedEnabled}
            : item,
        ),
      );
      setDraftDriverStates(prev => ({...prev, [driverId]: resolvedEnabled}));
      toast.success("Driver ride share access updated successfully");
    } catch (err) {
      setDraftDriverStates(prev => ({
        ...prev,
        [driverId]: !enabled,
      }));
      toast.error(getError(err));
    } finally {
      setSavingDriverIds(prev => ({...prev, [driverId]: false}));
    }
  };

  const updateSearchQuery = (setter, field, value) => {
    setter(prev => {
      const next = {...prev};
      if (value) {
        next[field] = value;
      } else {
        delete next[field];
      }
      return next;
    });
  };

  const renderToggle = ({checked, disabled = false, onChange}) => (
    <Switch
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className={`${
        checked ? "bg-indigo-600" : "bg-gray-300"
      } relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
      }`}>
      <span className="sr-only">Toggle ride share access</span>
      <span
        aria-hidden="true"
        className={`${
          checked ? "translate-x-7" : "translate-x-1"
        } pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg ring-0 transition duration-200`}
      />
    </Switch>
  );

  const renderTabButton = (tab, label) => {
    const active = activeTab === tab;
    return (
      <button
        type="button"
        onClick={() => setActiveTab(tab)}
        className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
          active
            ? "bg-indigo-600 text-white shadow"
            : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
        }`}>
        {label}
      </button>
    );
  };

  const passengerRows = useMemo(
    () =>
      passengers.map(passenger => ({
        id: passenger._id,
        name:
          passenger?.name ||
          [passenger?.firstName, passenger?.lastName]
            .filter(Boolean)
            .join(" ")
            .trim() ||
          "N/A",
        mobile: passenger?.mobile || "N/A",
        enabled:
          typeof draftPassengerStates[passenger._id] === "boolean"
            ? draftPassengerStates[passenger._id]
            : passenger?.rideShareAccessEnabled !== false,
      })),
    [draftPassengerStates, passengers],
  );

  const driverRows = useMemo(
    () =>
      drivers.map(driver => ({
        id: driver._id,
        name:
          driver?.name ||
          [driver?.firstName, driver?.lastName].filter(Boolean).join(" ").trim() ||
          "N/A",
        mobile: driver?.mobile || "N/A",
        enabled:
          typeof draftDriverStates[driver._id] === "boolean"
            ? draftDriverStates[driver._id]
            : driver?.rideShareAccessEnabled !== false,
      })),
    [draftDriverStates, drivers],
  );

  const accessSections = {
    passenger: {
      globalTitle: "Passenger App Access",
      globalDescription:
        "When disabled, the passenger app hides the ride share switcher and stays on rental flow only.",
      globalLabel: "Passenger ride share",
      searchQuery: passengerSearchQuery,
      updateSearch: (field, value) =>
        updateSearchQuery(setPassengerSearchQuery, field, value),
      onSearch: async () => {
        setPassengerPage(1);
        await loadPassengers({page: 1});
      },
      onClear: async () => {
        setPassengerSearchQuery({});
        setPassengerPage(1);
        await loadPassengers({page: 1, search: {}});
      },
      loading: passengersLoading,
      rows: passengerRows,
      onToggleUser: async (id, enabled) => {
        setDraftPassengerStates(prev => ({...prev, [id]: enabled}));
        await savePassengerAccess(id, enabled);
      },
      isSavingUser: id => !!savingPassengerIds[id],
      emptyText: "No passengers found.",
      page: passengerPage,
      setPage: setPassengerPage,
      limit: passengerLimit,
      setLimit: setPassengerLimit,
      totalPages: passengerTotalPages,
      enabled: passengerEnabled,
      setEnabled: setPassengerEnabled,
      isSavingGlobal: savingGlobal.passenger,
      onToggleGlobal: async enabled => {
        setPassengerEnabled(enabled);
        await saveGlobalAccess(PASSENGER_TAB, enabled);
      },
    },
    driver: {
      globalTitle: "Driver App Access",
      globalDescription:
        "When disabled, the driver app hides the ride share switcher and stays on rental flow only.",
      globalLabel: "Driver ride share",
      searchQuery: driverSearchQuery,
      updateSearch: (field, value) =>
        updateSearchQuery(setDriverSearchQuery, field, value),
      onSearch: async () => {
        setDriverPage(1);
        await loadDrivers({page: 1});
      },
      onClear: async () => {
        setDriverSearchQuery({});
        setDriverPage(1);
        await loadDrivers({page: 1, search: {}});
      },
      loading: driversLoading,
      rows: driverRows,
      onToggleUser: async (id, enabled) => {
        setDraftDriverStates(prev => ({...prev, [id]: enabled}));
        await saveDriverAccess(id, enabled);
      },
      isSavingUser: id => !!savingDriverIds[id],
      emptyText: "No drivers found.",
      page: driverPage,
      setPage: setDriverPage,
      limit: driverLimit,
      setLimit: setDriverLimit,
      totalPages: driverTotalPages,
      enabled: driverEnabled,
      setEnabled: setDriverEnabled,
      isSavingGlobal: savingGlobal.driver,
      onToggleGlobal: async enabled => {
        setDriverEnabled(enabled);
        await saveGlobalAccess(DRIVER_TAB, enabled);
      },
    },
  };

  const currentSection = accessSections[activeTab];

  return (
    <Layout title={sideBarItems.rideShareAccess.key}>
      <div className="w-full py-6">
        <h1 className="mb-6 text-2xl font-bold text-gray-900">
          Ride Share Access
        </h1>

        <div className="mb-6 flex items-center gap-3">
          {renderTabButton(PASSENGER_TAB, "Passenger")}
          {renderTabButton(DRIVER_TAB, "Driver")}
        </div>

        <div className="mb-6 rounded-lg bg-white p-6 shadow-md">
          <h2 className="text-lg font-semibold text-gray-900">
            {currentSection.globalTitle}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {currentSection.globalDescription}
          </p>

          <div className="mt-6 flex items-center justify-between gap-4">
            <span className="text-sm font-medium text-gray-700">
              {currentSection.globalLabel}
            </span>
            {renderToggle({
              checked: !!currentSection.enabled,
              disabled: loadingConfig || currentSection.isSavingGlobal,
              onChange: async () => {
                const nextEnabled = !currentSection.enabled;
                await currentSection.onToggleGlobal(nextEnabled);
              },
            })}
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow-md">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            {activeTab === PASSENGER_TAB
              ? "Passenger-Specific Access"
              : "Driver-Specific Access"}
          </h2>
          <p className="mb-4 text-sm text-gray-500">
            {activeTab === PASSENGER_TAB
              ? "Enable or disable ride share for specific passengers. The global passenger switch above still works as the master control."
              : "Enable or disable ride share for specific drivers. The global driver switch above still works as the master control."}
          </p>

          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
            <input
              type="text"
              placeholder="Search by name"
              value={currentSection.searchQuery.name || ""}
              onChange={e =>
                currentSection.updateSearch("name", e.target.value)
              }
              className="rounded border px-3 py-2"
            />
            <input
              type="text"
              placeholder="Search by mobile"
              value={currentSection.searchQuery.mobile || ""}
              onChange={e =>
                currentSection.updateSearch("mobile", e.target.value)
              }
              className="rounded border px-3 py-2"
            />
            <button
              onClick={currentSection.onSearch}
              className="rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700">
              Search
            </button>
            <button
              onClick={currentSection.onClear}
              className="rounded-md bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200">
              Clear
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200">
              <thead>
                <tr className="bg-gray-50 text-left text-sm text-gray-600">
                  <th className="border px-4 py-2">
                    {activeTab === PASSENGER_TAB ? "Passenger" : "Driver"}
                  </th>
                  <th className="border px-4 py-2">Mobile</th>
                  <th className="border px-4 py-2">Access</th>
                </tr>
              </thead>
              <tbody>
                {currentSection.loading ? (
                  <tr>
                    <td
                      colSpan="3"
                      className="border px-4 py-10 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : currentSection.rows.length ? (
                  currentSection.rows.map(row => (
                    <tr key={row.id} className="text-sm">
                      <td className="border px-4 py-2">
                        <div className="font-medium">{row.name}</div>
                        <div className="text-xs text-gray-500">{row.id}</div>
                      </td>
                      <td className="border px-4 py-2">{row.mobile}</td>
                      <td className="border px-4 py-2">
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-sm text-gray-700">
                            {row.enabled ? "Enabled" : "Disabled"}
                          </span>
                          {renderToggle({
                            checked: row.enabled,
                            disabled: currentSection.isSavingUser(row.id),
                            onChange: async () => {
                              await currentSection.onToggleUser(
                                row.id,
                                !row.enabled,
                              );
                            },
                          })}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="3"
                      className="border px-4 py-10 text-center text-gray-500">
                      {currentSection.emptyText}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex items-center justify-center space-x-4">
            <Pagination
              page={currentSection.page}
              totalPages={currentSection.totalPages}
              setPage={currentSection.setPage}
              limit={currentSection.limit}
              setLimit={currentSection.setLimit}
              prevPage={() =>
                currentSection.setPage(prev => Math.max(1, prev - 1))
              }
              nextPage={() =>
                currentSection.setPage(prev =>
                  Math.min(currentSection.totalPages, prev + 1),
                )
              }
            />
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default RideShareAccessPage;
